import type { LearnerJoinRequest } from "@history-sim/api-contracts";
import { resolveLearnerApiConfiguration } from "./config";
import { LearnerApiClient } from "./learnerApiClient";
import { type ConnectLearnerResult, LearnerSyncCoordinator } from "./learnerSyncCoordinator";
import { IndexedDbLearningOutbox } from "./learningOutbox";
import { useLearnerSyncStatus } from "./syncStatus";

// The origin is injected at build/dev startup; no credential or path is embedded in the client.
export const learnerSyncConfiguration = resolveLearnerApiConfiguration(import.meta.env);
const LOCAL_ONLY_SESSION_KEY = "history-sim-local-only-session-v1";

export interface LearnerSyncRuntime {
  readonly coordinator: LearnerSyncCoordinator;
  readonly outbox: IndexedDbLearningOutbox;
}

let runtime: LearnerSyncRuntime | undefined;

export function getLearnerSyncRuntime(): LearnerSyncRuntime | undefined {
  if (learnerSyncConfiguration.mode === "local-only") return undefined;
  runtime ??= (() => {
    const outbox = new IndexedDbLearningOutbox();
    return {
      outbox,
      coordinator: new LearnerSyncCoordinator(
        new LearnerApiClient(learnerSyncConfiguration.baseUrl),
        outbox,
      ),
    };
  })();
  return runtime;
}

export async function connectLearnerForSync(input: {
  readonly clientSessionId: string;
  readonly contentVersion: string;
  readonly appVersion: string;
  readonly identity: LearnerJoinRequest;
}): Promise<ConnectLearnerResult | undefined> {
  const current = getLearnerSyncRuntime();
  if (!current) return undefined;
  useLearnerSyncStatus.getState().setStatus("connecting");
  try {
    const connected = await current.coordinator.connect(input);
    try {
      sessionStorage.removeItem(LOCAL_ONLY_SESSION_KEY);
    } catch {
      // A restricted browser may not expose session storage.
    }
    useLearnerSyncStatus.getState().setStatus("synced");
    return connected;
  } catch (error) {
    useLearnerSyncStatus.getState().setStatus("error", {
      reasonCode:
        error && typeof error === "object" && "code" in error && typeof error.code === "string"
          ? error.code
          : "SYNC_CONNECT_FAILED",
    });
    throw error;
  }
}

export async function recoverLearnerForSync(input: {
  readonly clientSessionId: string;
  readonly contentVersion: string;
  readonly appVersion: string;
  readonly recoveryCode: string;
}): Promise<ConnectLearnerResult | undefined> {
  const current = getLearnerSyncRuntime();
  if (!current) return undefined;
  useLearnerSyncStatus.getState().setStatus("connecting");
  try {
    const connected = await current.coordinator.recover(input);
    try {
      sessionStorage.removeItem(LOCAL_ONLY_SESSION_KEY);
    } catch {
      // A restricted browser may not expose session storage.
    }
    useLearnerSyncStatus.getState().setStatus("synced");
    return connected;
  } catch (error) {
    useLearnerSyncStatus.getState().setStatus("error", {
      reasonCode:
        error && typeof error === "object" && "code" in error && typeof error.code === "string"
          ? error.code
          : "SYNC_RECOVERY_FAILED",
    });
    throw error;
  }
}

export function markLocalOnlyLearnerSession(clientSessionId: string): void {
  try {
    sessionStorage.setItem(LOCAL_ONLY_SESSION_KEY, clientSessionId);
  } catch {
    // A restricted browser may not expose session storage.
  }
  useLearnerSyncStatus.getState().setStatus("local-only");
}

export function isLocalOnlyLearnerSession(clientSessionId: string): boolean {
  try {
    return sessionStorage.getItem(LOCAL_ONLY_SESSION_KEY) === clientSessionId;
  } catch {
    return false;
  }
}
